import clsx from "clsx";
import { Search } from "lucide-react";
import { useDeferredValue, useState } from "react";

import type { ToolUsageRow } from "@/lib/types";

import { formatDuration, formatInt } from "./formatters";

// ---------------------------------------------------------------------------
// Grouping logic — works for any "prefix:" tool pattern (bash:*, skill:*, …)
// ---------------------------------------------------------------------------

/** Prefixes that should be grouped into collapsible parent rows. */
const GROUP_PREFIXES = ["sherlog:", "bash:", "skill:"] as const;

type ToolGroup = {
  /** The group name used as the parent row label, e.g. "bash" or "skill" */
  name: string;
  /** The prefix string including the colon, e.g. "bash:" */
  prefix: string;
  /** Aggregated parent row */
  parent: ToolUsageRow;
  /** Individual sub-category rows sorted by calls desc */
  subRows: ToolUsageRow[];
};

function groupToolRows(data: ToolUsageRow[]): {
  regular: ToolUsageRow[];
  groups: ToolGroup[];
} {
  const regular: ToolUsageRow[] = [];
  const buckets: Record<string, ToolUsageRow[]> = {};

  for (const row of data) {
    const prefix = GROUP_PREFIXES.find((p) => row.tool.startsWith(p));
    if (prefix) {
      (buckets[prefix] ??= []).push(row);
    } else {
      regular.push(row);
    }
  }

  const groups: ToolGroup[] = [];

  for (const prefix of GROUP_PREFIXES) {
    const children = buckets[prefix];
    if (!children || children.length === 0) continue;

    const totalCalls = children.reduce((sum, r) => sum + r.calls, 0);
    const measuredCalls = children.reduce((sum, row) => sum + row.measuredCalls, 0);
    const timedCalls = children.reduce((sum, row) => sum + row.timedCalls, 0);
    const totalErrors = children.reduce((sum, r) => sum + (r.errors ?? 0), 0);
    const totalDurationWeighted = children.reduce(
      (sum, r) => sum + (r.avgDurationMs ?? 0) * r.timedCalls,
      0,
    );

    children.sort((a, b) => b.calls - a.calls);

    groups.push({
      name: prefix.slice(0, -1), // "bash:" → "bash"
      prefix,
      parent: {
        tool: prefix.slice(0, -1),
        calls: totalCalls,
        measuredCalls,
        timedCalls,
        errors: measuredCalls ? totalErrors : null,
        errorRate: measuredCalls > 0 ? Math.round((totalErrors / measuredCalls) * 1000) / 10 : null,
        avgDurationMs: timedCalls > 0 ? Math.round(totalDurationWeighted / timedCalls) : null,
      },
      subRows: children,
    });
  }

  return { regular, groups };
}

// ---------------------------------------------------------------------------
// Row components
// ---------------------------------------------------------------------------

function ToolRow({ row, prefix }: { row: ToolUsageRow; prefix?: string }) {
  const indent = !!prefix;
  const displayName = prefix ? row.tool.slice(prefix.length) : row.tool;

  return (
    <tr className="border-cyber-border/20 hover:bg-cyber-surface/30 border-b transition-colors">
      <td className={clsx("text-cyber-text py-1.5 pr-4 font-mono", indent && "pl-5")}>
        {indent && <span className="text-cyber-muted/40 mr-1">{">"}</span>}
        {displayName}
      </td>
      <td className="text-cyber-muted py-1.5 pr-4 text-right">
        {formatInt(row.calls)}{" "}
        <span title="Calls with direct measurements">({row.measuredCalls} observed)</span>
      </td>
      <td className="text-cyber-muted py-1.5 pr-4 text-right">
        {row.errors === null ? "—" : formatInt(row.errors)}
      </td>
      <td
        className={clsx(
          "py-1.5 pr-4 text-right",
          (row.errorRate ?? 0) > 20 ? "font-medium text-red-400" : "text-cyber-muted",
        )}
      >
        {row.errorRate === null ? "—" : `${row.errorRate}%`}
      </td>
      <td className="text-cyber-muted py-1.5 text-right">
        {row.avgDurationMs === null ? "—" : formatDuration(row.avgDurationMs)}
      </td>
    </tr>
  );
}

function GroupRows({
  group,
  expanded,
  onToggle,
}: {
  group: ToolGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { parent, subRows, name, prefix } = group;

  return (
    <>
      {/* Parent row — clickable toggle */}
      <tr
        className="border-cyber-border/20 hover:bg-cyber-surface/30 cursor-pointer border-b transition-colors"
        onClick={onToggle}
      >
        <td className="text-cyber-text py-1.5 pr-4 font-mono">
          <span className="text-cyber-muted/60 mr-1 inline-block w-3 text-center">
            {expanded ? "-" : "+"}
          </span>
          {name}
          <span className="text-cyber-muted/50 ml-1.5 text-[10px]">
            ({subRows.length} sub-categories)
          </span>
        </td>
        <td className="text-cyber-muted py-1.5 pr-4 text-right">
          {formatInt(parent.calls)}{" "}
          <span title="Calls with direct measurements">({parent.measuredCalls} observed)</span>
        </td>
        <td className="text-cyber-muted py-1.5 pr-4 text-right">
          {parent.errors === null ? "—" : formatInt(parent.errors)}
        </td>
        <td
          className={clsx(
            "py-1.5 pr-4 text-right",
            (parent.errorRate ?? 0) > 20 ? "font-medium text-red-400" : "text-cyber-muted",
          )}
        >
          {parent.errorRate === null ? "—" : `${parent.errorRate}%`}
        </td>
        <td className="text-cyber-muted py-1.5 text-right">
          {parent.avgDurationMs === null ? "—" : formatDuration(parent.avgDurationMs)}
        </td>
      </tr>

      {/* Indented children */}
      {expanded && subRows.map((row) => <ToolRow key={row.tool} row={row} prefix={prefix} />)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ToolUsageTable({ data }: { data: ToolUsageRow[] }) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const deferredSearch = useDeferredValue(search).trim().toLowerCase();

  if (data.length === 0) {
    return <p className="text-cyber-muted/60 text-xs">No tool usage data available.</p>;
  }

  const filteredData = deferredSearch
    ? data.filter((row) => row.tool.toLowerCase().includes(deferredSearch))
    : data;
  const { regular, groups } = groupToolRows(filteredData);

  // Build group lookup for rendering
  const groupByName = new Map<string, ToolGroup>();
  for (const g of groups) groupByName.set(g.name, g);

  // Merge regular rows + group parent rows, sorted by calls descending
  const sortedRows: ToolUsageRow[] = [...regular];
  for (const g of groups) sortedRows.push(g.parent);
  sortedRows.sort((a, b) => b.calls - a.calls);
  const visibleRows = showAll || deferredSearch ? sortedRows : sortedRows.slice(0, 25);

  const toggleGroup = (name: string) =>
    setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }));

  return (
    <div>
      <p className="text-cyber-muted mb-3 text-xs">
        Errors are framework errors from observed calls. Durations are recorded tool spans, not wall
        time. Legacy measurements are unavailable.
      </p>
      <div className="relative mb-3">
        <Search
          size={13}
          className="text-cyber-muted pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
        />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search tools"
          aria-label="Search tool usage"
          className="border-cyber-border bg-cyber-bg/50 text-cyber-text placeholder:text-cyber-muted/60 focus:border-cyber-cyan/60 w-full rounded border py-2 pr-3 pl-8 text-xs transition-colors outline-none"
        />
      </div>

      {visibleRows.length === 0 ? (
        <p className="text-cyber-muted/60 py-6 text-center text-xs">No tools match this search.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-cyber-border/50 text-cyber-muted/70 border-b text-left text-[10px] tracking-wide uppercase">
                <th className="pr-4 pb-2 font-medium">Tool</th>
                <th className="pr-4 pb-2 text-right font-medium">Calls</th>
                <th className="pr-4 pb-2 text-right font-medium">Errors</th>
                <th className="pr-4 pb-2 text-right font-medium">Error %</th>
                <th className="pb-2 text-right font-medium">Avg Duration</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const group = groupByName.get(row.tool);
                return group ? (
                  <GroupRows
                    key={group.name}
                    group={group}
                    expanded={!!deferredSearch || !!expandedGroups[group.name]}
                    onToggle={() => toggleGroup(group.name)}
                  />
                ) : (
                  <ToolRow key={row.tool} row={row} />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!deferredSearch && sortedRows.length > 25 && (
        <div className="border-cyber-border/30 text-cyber-muted mt-3 flex items-center justify-between border-t pt-3 text-[11px]">
          <span>
            Showing {showAll ? sortedRows.length : 25} of {sortedRows.length} entries
          </span>
          <button
            type="button"
            onClick={() => setShowAll((current) => !current)}
            className="text-cyber-cyan hover:text-cyber-text transition-colors"
          >
            {showAll ? "Show top 25" : "Show all"}
          </button>
        </div>
      )}
    </div>
  );
}
